/**
 * React Query hooks for AI Configuration operations
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  aiConfigApi,
  CreateAIConfigRequest,
  UpdateAIConfigRequest,
} from '../../api/ai-config.api';

/**
 * Query keys for AI configurations
 */
export const aiConfigKeys = {
  all: ['ai-configs'] as const,
  lists: () => [...aiConfigKeys.all, 'list'] as const,
  list: () => [...aiConfigKeys.lists()] as const,
  details: () => [...aiConfigKeys.all, 'detail'] as const,
  detail: (id: string) => [...aiConfigKeys.details(), id] as const,
  default: () => [...aiConfigKeys.all, 'default'] as const,
  overrideStatus: () => [...aiConfigKeys.all, 'override-status'] as const,
};

/**
 * Hook to fetch all AI configurations
 */
export function useAIConfigs() {
  return useQuery({
    queryKey: aiConfigKeys.list(),
    queryFn: async () => {
      try {
        console.log('[useAIConfigs] Fetching AI configurations...');
        const result = await aiConfigApi.getConfigurations();
        console.log('[useAIConfigs] Success:', result);
        return result;
      } catch (error) {
        console.error('[useAIConfigs] Error:', error);
        if (error instanceof Error) {
          console.error('[useAIConfigs] Error message:', error.message);
          console.error('[useAIConfigs] Error stack:', error.stack);
        }
        throw error;
      }
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: 3, // Retry failed requests 3 times
    retryDelay: 1000, // Wait 1 second between retries
  });
}

/**
 * Hook to fetch a specific AI configuration
 */
export function useAIConfig(id: string | undefined) {
  return useQuery({
    queryKey: aiConfigKeys.detail(id!),
    queryFn: () => aiConfigApi.getConfiguration(id!),
    enabled: !!id,
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Hook to fetch the default AI configuration
 */
export function useDefaultAIConfig() {
  return useQuery({
    queryKey: aiConfigKeys.default(),
    queryFn: () => aiConfigApi.getDefaultConfiguration(),
    staleTime: 5 * 60 * 1000,
    retry: false, // Don't retry if no default is set
  });
}

/**
 * Hook to create a new AI configuration
 */
export function useCreateAIConfig() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateAIConfigRequest) => aiConfigApi.createConfiguration(data),
    onSuccess: (newConfig) => {
      // Invalidate and refetch configurations list
      queryClient.invalidateQueries({ queryKey: aiConfigKeys.lists() });
      
      // If this is the default config, invalidate default query
      if (newConfig.isDefault) {
        queryClient.invalidateQueries({ queryKey: aiConfigKeys.default() });
      }
      
      // Add the new config to the cache
      queryClient.setQueryData(aiConfigKeys.detail(newConfig.id), newConfig);
    },
  });
}

/**
 * Hook to update an AI configuration
 */
export function useUpdateAIConfig() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateAIConfigRequest }) =>
      aiConfigApi.updateConfiguration(id, data),
    onSuccess: (updatedConfig) => {
      // Update the specific config in cache
      queryClient.setQueryData(aiConfigKeys.detail(updatedConfig.id), updatedConfig);
      
      // Invalidate lists to reflect changes
      queryClient.invalidateQueries({ queryKey: aiConfigKeys.lists() });
      
      // If default status changed, invalidate default query
      if (updatedConfig.isDefault) {
        queryClient.invalidateQueries({ queryKey: aiConfigKeys.default() });
      }
    },
  });
}

/**
 * Hook to delete an AI configuration
 */
export function useDeleteAIConfig() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => aiConfigApi.deleteConfiguration(id),
    onSuccess: (_, deletedId) => {
      // Remove from cache
      queryClient.removeQueries({ queryKey: aiConfigKeys.detail(deletedId) });
      
      // Invalidate lists
      queryClient.invalidateQueries({ queryKey: aiConfigKeys.lists() });
      
      // Invalidate default (in case the deleted one was default)
      queryClient.invalidateQueries({ queryKey: aiConfigKeys.default() });
    },
  });
}

/**
 * Hook to test an API key
 */
export function useTestAPIKey() {
  return useMutation({
    mutationFn: (id: string) => aiConfigApi.testApiKey(id),
  });
}

/**
 * Hook to update usage statistics
 */
export function useUpdateAIConfigUsage() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => aiConfigApi.updateUsage(id),
    onSuccess: (_, id) => {
      // Invalidate the specific config to refetch updated usage stats
      queryClient.invalidateQueries({ queryKey: aiConfigKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: aiConfigKeys.lists() });
    },
  });
}

/**
 * Hook to get active configurations (helper)
 */
export function useActiveAIConfigs() {
  const { data: configs, ...rest } = useAIConfigs();
  
  return {
    ...rest,
    data: configs?.filter((config) => config.isActive) ?? [],
  };
}

/**
 * Hook to get configurations by provider (helper)
 */
export function useAIConfigsByProvider(provider: string) {
  const { data: configs, ...rest } = useAIConfigs();
  
  return {
    ...rest,
    data: configs?.filter((config) => config.provider === provider) ?? [],
  };
}

/**
 * Hook to fetch the platform-global LLM override status.
 * When overrideActive=true, per-user AI configuration is not needed and the
 * "No AI provider configured" banner should be suppressed.
 */
export function useLlmOverrideStatus() {
  return useQuery({
    queryKey: aiConfigKeys.overrideStatus(),
    queryFn: () => aiConfigApi.getOverrideStatus(),
    // Override status rarely changes — long stale time
    staleTime: 5 * 60 * 1_000,
  });
}

// Made with Bob
